import { MouseEvent, useRef, useState } from "react";
import Cropper, { ReactCropperElement } from "react-cropper";
import styled from "styled-components";

import useCreatePin from "apis/mutations/pin/useCreatePin";

import Scene from "types/Scene";
import SceneImageMode from "types/SceneImgMode";

import HEADER_HEIGHT from "components/atoms/headerHeight";
import SIDE_BAR_WIDTH from "components/atoms/sideBarWidth";
import Pin from "components/templates/project/Scene/Pin";

interface Props {
  productOptionId: number;
  currentScene?: Scene;
  sceneImageMode: SceneImageMode;
  onClickPin: (params: { productId: number; pinId: number }) => void;
  pinId: number;
  productId: number;
  onChangeCropMode: () => void;
  cropBoxData: Cropper.CropBoxData | null;
}

// 일반 이미지 모드 - 핀 생성, 핀 조회에서 사용
// Crop 모드와 동일한 이미지 비율로 맞추기 위해서 크롭모드는 꺼진 상태로 Cropper 라이브러리 적용
const SceneImage = ({
  productOptionId,
  currentScene,
  sceneImageMode,
  onClickPin,
  pinId,
  productId,
  onChangeCropMode,
  cropBoxData,
}: Props) => {
  const ref = useRef<ReactCropperElement>(null);
  const [canvasData, setCanvasData] = useState<Cropper.CanvasData>();

  // 크롭모드로 돌아가기 이벤트 -> api 요청 시 실행할 함수
  const handleBackCropMode = () => {
    if (cropBoxData) {
      setTimeout(onChangeCropMode, 1000);
    }
  };

  // 핀 생성 api
  const createPin = useCreatePin({
    projectId: currentScene?.projectId,
    onSuccess: () => {
      handleBackCropMode();
    },
    onError: () => {
      handleBackCropMode();
    },
  });

  if (!currentScene) return null;

  // 샘플 적용 버튼 클릭 이벤트 => 핀생성
  // 위치는 이미지 사이즈의 퍼센트로 계산하여 DB에 저장
  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (sceneImageMode !== "pin") return;
    if (!ref.current) return;

    const instance = ref.current.cropper;
    const canvas = instance.getCanvasData();

    const x = e.clientX - canvas.left - SIDE_BAR_WIDTH;
    const y = e.clientY - canvas.top - HEADER_HEIGHT;

    const isPossibleCoordinate =
      x >= 0 && x <= canvas.width && y >= 0 && y <= canvas.height;

    if (isPossibleCoordinate) {
      const xCoordinate = ((x / canvas.width) * 100).toString();
      const yCoordinate = ((y / canvas.height) * 100).toString();

      const pinData = {
        xCoordinate,
        yCoordinate,
        sceneId: currentScene.id,
        productOptionId,
      };

      createPin(pinData);
    }
  };

  const cropperProps = {
    ref: ref,
    src: currentScene.image,
    dragMode: "none" as Cropper.DragMode,
    autoCrop: false,
    initialAspectRatio: 1,
    background: false,
    scalable: false,
    zoomable: false,
    checkCrossOrigin: false,
    checkOrientation: false,
    viewMode: 0 as Cropper.ViewMode,
    style: { width: "100%", height: "100%" },
    ready: () => {
      const cropperInstance = ref.current?.cropper;
      const canvas = cropperInstance?.getCanvasData();
      setCanvasData(canvas);
    },
  };

  return (
    <div className="relative w-full h-full" onClick={handleClick}>
      <Cropper {...cropperProps} />

      <PinWrap
        width={canvasData?.width}
        height={canvasData?.height}
        top={canvasData?.top}
        left={canvasData?.left}
      >
        {currentScene?.pins.map((pin) => (
          <Pin
            key={pin.id}
            pin={pin}
            pinId={pinId}
            productId={productId}
            onClickPin={() =>
              onClickPin({
                pinId: pin.id,
                productId: pin.productOption.productId,
              })
            }
          />
        ))}
      </PinWrap>
    </div>
  );
};

const PinWrap = styled.div<{
  width?: number;
  height?: number;
  top?: number;
  left?: number;
}>`
  position: absolute;
  top: ${({ top }) => top || 0}px;
  left: ${({ left }) => left || 0}px;
  width: ${({ width }) => width || 0}px;
  height: ${({ height }) => height || 0}px;
`;

export default SceneImage;
